import GoogleMapReact from "google-map-react"
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Supercluster from 'supercluster';
import { extendedBounds, fetchMapPoints, filterPointsOutOfMap, mapSettings, haversineDistance, pointArrayLongFormat, filterPointsByCountry } from "./mapUtils";
import { FiltersStateContext } from "shared-components/Contexts/Filters/context";
import { getOnlyValues } from "shared-components/Components/Filters/Filters.hook";
import { Marker } from "./Marker";
import { InfoWindow } from "./InfoWindow";
import { AskForDraw, DrawBtn } from "./Drawing";
import { MapLoader } from "./MapLoader";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import { useWindowSize } from "shared-components/GlobalHooks/useWindowSize";
import { useSearchResultsFilters } from "shared-components/ViewFragments/SearchResults/Sider/Filters/SearchResultsFilters.hook";
import { getPolygonLocations } from "src/services/search";

export const calculateVisibleItems = (containerHeight) => {
    let itemsQty = 0;
    const totalItemHeight = 175 + 10;

    itemsQty = Math.floor(containerHeight / totalItemHeight);

    return itemsQty;
};

export const MapSearch = () => {
    const filters = useContext(FiltersStateContext)
    const { changeFiltersAndUpdateURL } = useSearchResultsFilters();
    const { country_code, main_domain } = useContext(ConfigStateContext);
    const screen = useWindowSize()
    const isDrawingRef = useRef(false);
    const [points, setPoints] = useState([])
    const [sortedPoints, setSortedPoints] = useState([])
    const [bounds, setBounds] = useState(null)
    const [zoom, setZoom] = useState(14)
    const [drawing, setDrawing] = useState(false)
    const [drawingModal, setDrawingModal] = useState(true)
    const [polygon, setPolygon] = useState(null)
    const [center, setCenter] = useState(mapSettings(country_code).dafaults.center)
    const MAP = useRef(null);
    const MAPS = useRef(null);
    const pointsRef = useRef([]);
    const polygonRef = useRef(null);
    const eventMap = typeof window !== "undefined" && "ontouchstart" in document.documentElement ? "touchstart" : "mousedown";
    const [loading, setLoading] = useState(false)
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [isCluster, setIsCluster] = useState(false);
    const [clickedMarkers, setClickedMarkers] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [carouselData, setCarouselData] = useState([]);
    const [visibleItems, setVisibleItems] = useState(0);

    useEffect(() => {
        if ((carouselData.length < visibleItems && sortedPoints.length > 0) && visibleItems > 0) {
            setCarouselData(sortedPoints.slice(0, visibleItems));
        }
    }, [sortedPoints, visibleItems]);

    useEffect(() => {
        pointsRef.current = points
        fitMapToResults()
    }, [points])

    useEffect(() => {
        if (filters) {
            if (filters?.locations && !!filters?.locations[0]?.value?.length || !!filters?.locations?.value?.length) {
                const locations = Array.isArray(filters?.locations) ? filters?.locations : [filters?.locations]
                const ids = locations[0]?.value.map((location) => location.id)
                // resetDrawInMap(); //evaluar si descomentar, estaba causando un loop
    
                getLocationsPolygons(ids.join()).then((res) => {
                    const {polygons, locationsWithoutPolygons} = res;
                    
                    applyPolygonOnMap(polygons, locationsWithoutPolygons);
                    setPolygon(polygons)
                })

                setSelectedMarker(null);
            } else if (filters?.map_polygons) {
                if (isDrawingRef.current) {
                    isDrawingRef.current = false;
                    return;
                }

                const filterPolygon = filters?.map_polygons?.value.map(polygon => polygon.map((p) => ({ lat: p.latitude, lng: p.longitude })));

                setPolygon(filterPolygon);
                setSelectedMarker(null);
                applyPolygonOnMap(filterPolygon);
            } else if (!filters?.map_polygons && polygon) {
                resetMapState();
            }
        }
    }, [filters])
    
    useEffect(() => {
        if (!MAP.current && !MAPS.current) return;
        MAPS.current.drawing = drawing
        MAP.current.setOptions({
            clickableIcons: false,
            draggable: !drawing,
            draggableCursor: drawing ? "crosshair" : "grab",
            zoomControl: !drawing && (screen.xl),
            scrollwheel: !drawing,
            disableDoubleClickZoom: drawing,
        });
    }, [drawing])

    useEffect(() => {
        polygonRef.current = polygon
        updatePolygon();
    }, [polygon])

    useEffect(() => {
        const calculateAndSetVisibleItems = () => {
            const container = document.querySelector('.overlay-wrapper__container');

            if (container) {
                const containerHeight = container.getBoundingClientRect().height;
                const visible = calculateVisibleItems(containerHeight);

                setVisibleItems(visible + 3);
            }
        };

        calculateAndSetVisibleItems();

        const observer = new MutationObserver(() => {
            calculateAndSetVisibleItems();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [screen.viewportHeight, selectedMarker]);

    const loadMoreData = () => {
        setCarouselData(prevData => [
            ...prevData,
            ...sortedPoints.slice(prevData.length, prevData.length + visibleItems)
        ]);
    };

    const handleVisibleIdChange = (lat, lng, id, index = null) => {
        let latOffset = centerMarkerLatOffset();
        setCenter({ lat: parseFloat(lat) - latOffset, lng: parseFloat(lng) });
        setSelectedMarker(id);
        if (index) setCurrentIndex(Number(index));
    };

    const fitMapToCoordinates = (coordinates) => {
        if (!MAP.current || !MAPS.current || !coordinates?.length) return false;

        let bounds = new MAPS.current.LatLngBounds()
        coordinates.forEach(c => bounds.extend(c))

        MAP.current.fitBounds(bounds)
        return true;
    }

    const fitMapToPoints = (currentPoints) =>
        fitMapToCoordinates(currentPoints?.map(e => ({ lat: parseFloat(e.lat), lng: parseFloat(e.long) })))

    const fitMapToPolygon = (currentPolygon) => {
        if (!currentPolygon?.length) return false;

        //el polygon puede ser un unico anillo [{lat,lng}] o varios [[{lat,lng}]]
        const rings = Array.isArray(currentPolygon[0]) ? currentPolygon : [currentPolygon];
        const coordinates = [];
        rings.forEach(ring => ring?.forEach(point => coordinates.push(point)));

        return fitMapToCoordinates(coordinates);
    }

    //encuadra sobre los puntos y, si no hay resultados, sobre el area buscada
    const fitMapToResults = () => {
        if (fitMapToPoints(pointsRef.current)) return;
        fitMapToPolygon(polygonRef.current);
    }

    //limpiar points cuando se limpian todas la ubicaciones

    const getLocationsPolygons = async (ids) => {
        const polygons = await getPolygonLocations(ids, main_domain);
        const locationsWithoutPolygons = [];
        const polygonData = [];

        polygons.map(({location: { id, type, multi_polygon: { coordinates } }}) => {
            if (coordinates) {
                const currentCoordinate = coordinates[0][0];
                const currentCoordinateData = currentCoordinate.map((c) => ({ lat: c[1], lng: c[0] }));

                polygonData.push(currentCoordinateData)
            } else {
                locationsWithoutPolygons.push({ id, type })
            }
        })

        return {
            polygons: polygonData,
            locationsWithoutPolygons
        }
    }

    const genClusters = () => {
        const extBounds = extendedBounds(bounds);
        let geoPoints = points;

        geoPoints = filterPointsOutOfMap(points, extBounds);
        geoPoints = geoPoints?.map(p => ({
            "type": "Feature",
            "properties": {
                "propID": p.id,
                ...p
            },
            "geometry": {
                "type": "Point",
                "coordinates": [parseFloat(p.lat), parseFloat(p.long)]
            }
        }));


        const index = new Supercluster({ radius: 60, extent: 200, maxZoom: 20 });
        index.load(geoPoints);
        const clusters = index.getClusters([-180, -85, 180, 85], zoom);

        const clustersWithPoints = clusters.map(cluster => {
            if (cluster.properties.cluster) {
                const clusterId = cluster.id;
                const clusteredPoints = index.getLeaves(clusterId, Infinity).sort((a, b) => a.properties.propID - b.properties.propID);
                cluster.properties.pointsData = clusteredPoints.map(point => point.properties);
                cluster.properties.randomID = btoa(JSON.stringify(cluster.properties.pointsData))
            }
            return cluster;
        });

        return clustersWithPoints;
    }

    const updatePolygon = () => {
        if (!MAP.current && !MAPS.current) return;

        if (MAPS.current.savedPolygon !== null) {
            MAPS.current.savedPolygon.setMap(null);
        }
        if (!polygon || polygon.length === 0) return;

        MAPS.current.savedPolygon = new MAPS.current.Polygon({
            paths: polygon,
            strokeColor: '#35394e',
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: '#A1ACE8',
            fillOpacity: 0.1,
            clickable: false,
            map: MAP.current,
            geodesic: true,
        });
    }

    const applyPolygonOnMap = (
        currentPolygon: any,
        polygonsWithourCoordinates?: any
    ) => {
        if (!currentPolygon) {
            resetDrawInMap();
            return null;
        }

        setLoading(true);

        fetchMapPoints(
            getOnlyValues(filters),
            {polygon: currentPolygon, polygonsWithourCoordinates},
            main_domain
        ).then(point => {
            const pointsOnlyCurrentCountry = filterPointsByCountry(country_code, point);
                                
            if (pointsOnlyCurrentCountry) {
                setPoints(pointsOnlyCurrentCountry)
                setLoading(false)
            }
        });
        
        if (
            currentPolygon && !!currentPolygon.length &&
            (!filters?.locations || (filters?.locations && !filters?.locations[0]?.value.length))
        ) {
            const polygonsLongFormat = Array.isArray(currentPolygon[0]) ? currentPolygon.map(polygon => pointArrayLongFormat(polygon)) : pointArrayLongFormat(currentPolygon)

            changeFiltersAndUpdateURL({
                mapView: true,
                map_polygons: { "text": "Dibujo en Mapa", "value":  polygonsLongFormat},
            });
        }
    }

    const applyDraw = (polyPoints) => {
        isDrawingRef.current = true;
        setDrawing(false)
        setPolygon([polyPoints])
        applyPolygonOnMap([polyPoints])
    }

    const setupMapListeners = useCallback(() => {
        if (!MAP.current || !MAPS.current) return;
    
        if (MAPS.current.listeners) {
            MAPS.current.listeners.forEach(listener => {
                MAPS.current.event.removeListener(listener);
            });
        }
        MAPS.current.listeners = [];
    
        const domListener = MAPS.current.event.addDomListener(MAP.current.getDiv(), eventMap, function (e) {
            e.preventDefault();
            if (MAPS.current.drawing) {
                if (MAPS.current.drawingPolygon) {
                    MAPS.current.drawingPolygon.setMap(null);
                }
    
                let lineSettings = {
                    map: MAP.current,
                    clickable: false,
                    strokeColor: "#35394e",
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    fillOpacity: 0,
                    geodesic: true,
                };
    
                MAPS.current.drawingPolygon = new MAPS.current.Polyline(lineSettings);
            }
        });
    
        const mouseMoveListener = MAPS.current.event.addListener(MAP.current, "mousemove", function (e) {
            if (MAPS.current.drawing && MAPS.current.drawingPolygon) {
                let mainPath = MAPS.current.drawingPolygon.getPath();
                mainPath.push(e.latLng);
            }
        });
    
        const mouseUpListener = MAPS.current.event.addListener(MAP.current, "mouseup", function (e) {
            if (MAPS.current.drawing) {
                let polyPoints = MAPS.current.drawingPolygon
                    .getPath()
                    .getArray()
                    .map(o => ({
                        lat: o.lat(),
                        lng: o.lng(),
                    }));
    
                applyDraw(polyPoints);
                MAPS.current.drawingPolygon.setMap(null);
            }
        });
    
        MAPS.current.listeners = [domListener, mouseMoveListener, mouseUpListener];
    }, [filters, drawing]);

    const handleApiLoaded = (__map, __maps) => {
        MAP.current = __map;
        MAPS.current = __maps;
        MAPS.current.drawing = drawing;
        MAPS.current.drawingPolygon = null;
        MAPS.current.savedPolygon = null;
    
        setupMapListeners();
        updatePolygon();
        //los datos pueden haber llegado antes que la API de Google: sin esto el mapa
        //se queda en el centro por defecto del pais
        fitMapToResults();
    }
    
    useEffect(() => {
        setupMapListeners();
    }, [setupMapListeners]);

    const toggleDrawing = () => {
        let newVal = !drawing

        resetMapState()
        setDrawing(newVal)
    }

    const resetDrawInMap = () => {
        setSelectedMarker(null)
        setPolygon(null)
        setPoints([])
        setClickedMarkers([])
        setCurrentIndex(0)
        setCarouselData([])

        if ("map_polygons" in filters) {
            changeFiltersAndUpdateURL({
                map_polygons: null
            });
        }
    }

    const resetMapState = (val = true) => {
        if (val) {
            setDrawingModal(true)
        } else {
            setDrawingModal(false)
        }

        //parece que este cambio está haciendo que se ejecute 2 veces el map search, revisar
        if (polygon) {
            changeFiltersAndUpdateURL({
                map_polygons: null,
            });
        }

        if (filters?.locations && (!!filters?.locations.length || !!filters?.locations?.value?.length)) {
            changeFiltersAndUpdateURL({
                locations: []
            });
        }

        setPolygon(null)
        setSelectedMarker(null)
        setPoints([])
        setLoading(false)
        setClickedMarkers([])
        setCurrentIndex(0)
        setCarouselData([])
    }

    const centerMarkerLatOffset = () => {
        if (screen.xl || !MAP?.current) return 0

        let offset = 0
        const bounds = MAP?.current?.getBounds();
        const north = bounds?.getNorthEast().lat();
        const south = bounds?.getSouthWest().lat();
        if (north && south) {
            offset = (north - south) / 4
        }
        return offset
    }

    const _onChildClick = (key, childProps) => {
        setCurrentIndex(0)

        let latOffset = centerMarkerLatOffset()

        if (childProps?.pointObj?.properties?.cluster) {
            const clusterId = childProps.pointObj.properties.randomID;
            const clusteredPoints = childProps.pointObj.properties.pointsData || [];

            let newZoom = zoom >= 18 ? 18 : zoom + 1;
            if (newZoom < 14) newZoom = 14;
            setZoom(newZoom);

            if (zoom >= 16 || (clusteredPoints.length <= 20 && clusteredPoints.length !== 0)) {
                setSelectedMarker(clusterId);
                setIsCluster(true)
                setCurrentIndex(0)
                if (!clickedMarkers.includes(clusterId)) {
                    setClickedMarkers(prev => [...prev, clusterId]);
                }
            } else {
                latOffset = 0
            }

            setCenter({ lat: parseFloat(childProps?.lat) - latOffset, lng: parseFloat(childProps?.lng) });

        } else {
            setIsCluster(false)
            setCenter({ lat: parseFloat(childProps?.lat) - latOffset, lng: parseFloat(childProps?.lng) });
            const markerID = childProps?.pointObj?.properties?.propID;
            setSelectedMarker(markerID);

            if (!clickedMarkers.includes(markerID)) {
                setClickedMarkers(prev => [...prev, markerID]);
            }
        }

        const sortedPoints = points
            .map(point => {
                return ({
                    ...point,
                    distance: haversineDistance(childProps?.lat, childProps?.lng, point.lat, point.long)
                })
            })
            .sort((a, b) => a.distance - b.distance);

        setCarouselData([])
        setSortedPoints(sortedPoints)

    };

    const nextPreviousCallback = (type, index = null) => {
        const isNext = type === "next";
        const newIndex = currentIndex + (isNext ? 1 : -1);
        const settedIndex = index ?? newIndex;

        const { id, lat, long } = carouselData[settedIndex];
        handleVisibleIdChange(lat, long, id);

        if (isNext && (carouselData.length - currentIndex) <= visibleItems) {
            loadMoreData();
        }

        setCurrentIndex(settedIndex);
    };

    const paginatorInfo =
    {
        "firstItem": 0,
        "lastItem": 0,
        "lastPage": 0,
        "total": points?.length ?? 0,
        "currentPage": 0
    }

    return <>
        <div className="mapContainer" >
            <MapLoader active={loading} />

            {
                drawingModal && !drawing && 
                (!polygon || polygon.length <= 0) &&
                (filters?.locations && !filters?.locations[0]?.value.length || !filters?.locations) ? (
                    <AskForDraw close={() => { setDrawingModal(false) }} />
                ) : null
            }

            <InfoWindow
                selectedMarker={selectedMarker}
                closeCallback={() => setSelectedMarker(null)}
                isCluster={isCluster}
                nextPreviousCallback={(type, index) => nextPreviousCallback(type, index)}
                currentIndex={currentIndex}
                carouselData={carouselData}
                loadMoreData={loadMoreData}
                onVisibleIdChange={handleVisibleIdChange}
                paginatorInfo={paginatorInfo}
            />

            <GoogleMapReact
                bootstrapURLKeys={{ key: mapSettings(country_code).googleMapsApiKey }}
                defaultCenter={mapSettings(country_code).dafaults.center}
                defaultZoom={mapSettings(country_code).dafaults.zoom}
                options={{ 
                    ...mapSettings(country_code).options,
                    zoomControl: (screen.xl),
                    clickableIcons: false
                }}
                center={center}
                zoom={zoom}
                onChildClick={_onChildClick}
                onChange={({ center, zoom, bounds }) => {
                    setBounds(bounds)
                    setZoom(zoom)
                }}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={x => handleApiLoaded(x.map, x.maps)}
            >
                {!loading && genClusters()?.map((p, i) =>
                    <Marker key={`marker-${i}`}
                        pointObj={p}
                        lat={p.geometry.coordinates[0]}
                        lng={p.geometry.coordinates[1]}
                        clickedMarkers={clickedMarkers}
                        selectedMarker={selectedMarker}
                    />
                )}
            </GoogleMapReact>
            {(screen.xl || !screen.xl && !selectedMarker) &&
                <DrawBtn drawing={drawing} polygon={polygon} action={toggleDrawing} actionClearMap={() => resetMapState(false)} />
            }

        </div>
        <style jsx global>{`
             html, body {
                overscroll-behavior: none;
            }
        `}</style>
    </>

}